import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Save, Sparkles, ChevronDown, ChevronUp, CheckCircle2, XCircle,
  Edit3, Loader2, AlertTriangle, Info
} from 'lucide-react';
import axios from 'axios';

import { API_URL } from '../../utils/api';

const PWN_FIELDS = [
  { key: 'action_proposed', label: 'Action Proposed or Refused', hint: 'Describe the action the school district proposes or refuses to take.' },
  { key: 'description_of_action', label: 'Description of the Action', hint: 'Provide a detailed description of what will be done or what was refused.' },
  { key: 'reason_for_action', label: 'Reason for the Action', hint: 'Explain why this action is proposed or refused.' },
  { key: 'options_considered', label: 'Options Considered', hint: 'List other options the IEP team considered.' },
  { key: 'reason_options_rejected', label: 'Reasons Options Were Rejected', hint: 'Explain why other options were not chosen.' },
  { key: 'evaluation_data_used', label: 'Evaluation Procedures, Assessments, Records, or Reports Used', hint: 'List the data sources that informed this decision.' },
  { key: 'other_factors', label: 'Other Factors Relevant to the Decision', hint: 'Any additional information the parent should know.' },
];

const CONFIDENCE_STYLE = {
  high: 'bg-green-50 text-green-700 border-green-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low: 'bg-gray-50 text-gray-500 border-gray-200',
};

function mergeContent(existing) {
  const defaults = {};
  PWN_FIELDS.forEach(f => { defaults[f.key] = ''; });
  defaults._sources = {};
  if (!existing || typeof existing !== 'object') return defaults;
  if (existing.notes && !existing.action_proposed) {
    return { ...defaults, action_proposed: existing.notes };
  }
  return { ...defaults, ...existing };
}

export default function PWNEditor({ section, caseData, onSave, saving }) {
  const [content, setContent] = useState(() => mergeContent(section?.content));
  const [aiOpen, setAiOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [fieldDrafts, setFieldDrafts] = useState(null);
  const [aiMeta, setAiMeta] = useState(null);
  const [draftStates, setDraftStates] = useState({});

  useEffect(() => {
    setContent(mergeContent(section?.content));
  }, [section?.section_id]);

  const updateField = (key, value) => {
    setContent(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    const { _sources, ...saveData } = content;
    if (_sources && Object.keys(_sources).length > 0) {
      saveData._sources = _sources;
    }
    onSave(saveData);
  };

  // --- AI PWN Drafting ---
  const generateDrafts = async () => {
    setGenerating(true);
    setFieldDrafts(null);
    setAiMeta(null);
    setDraftStates({});
    try {
      const res = await axios.post(
        `${API_URL}/api/iep-met/cases/${caseData.case_id}/sections/pwn_summary/draft`,
        {}, { withCredentials: true }
      );
      const output = res.data?.output;
      if (output?.field_drafts) {
        setFieldDrafts(output.field_drafts);
        setAiMeta({
          dataSources: output.data_sources_used || [],
          notes: output.notes || '',
          gaps: output.gaps_identified || [],
          model: res.data?.model_used,
          inputSnapshot: res.data?.input_snapshot,
        });
        const initial = {};
        output.field_drafts.forEach(d => { initial[d.field_key] = 'pending'; });
        setDraftStates(initial);
      }
    } catch (err) {
      console.error('AI PWN drafting failed', err);
      setAiMeta({ error: err.response?.data?.detail || 'AI drafting failed. Please try again.' });
    } finally {
      setGenerating(false);
    }
  };

  const acceptDraft = (fieldKey) => {
    const draft = fieldDrafts.find(d => d.field_key === fieldKey);
    if (!draft) return;
    setContent(prev => ({
      ...prev,
      [fieldKey]: draft.draft_text,
      _sources: { ...(prev._sources || {}), [fieldKey]: 'ai_suggested' },
    }));
    setDraftStates(prev => ({ ...prev, [fieldKey]: 'accepted' }));
  };

  const editDraft = (fieldKey) => {
    const draft = fieldDrafts.find(d => d.field_key === fieldKey);
    if (!draft) return;
    setContent(prev => ({
      ...prev,
      [fieldKey]: draft.draft_text,
      _sources: { ...(prev._sources || {}), [fieldKey]: 'ai_edited' },
    }));
    setDraftStates(prev => ({ ...prev, [fieldKey]: 'edited' }));
  };

  const rejectDraft = (fieldKey) => {
    setDraftStates(prev => ({ ...prev, [fieldKey]: 'rejected' }));
  };

  const acceptAllPending = () => {
    if (!fieldDrafts) return;
    fieldDrafts.forEach(d => {
      if (draftStates[d.field_key] === 'pending') acceptDraft(d.field_key);
    });
  };

  const pendingCount = fieldDrafts ? Object.values(draftStates).filter(s => s === 'pending').length : 0;
  const filledCount = PWN_FIELDS.filter(f => (content[f.key] || '').trim()).length;

  const getDraftForField = (fieldKey) => fieldDrafts?.find(d => d.field_key === fieldKey);
  const getFieldSource = (fieldKey) => content._sources?.[fieldKey];

  return (
    <div className="max-w-4xl space-y-6" data-testid="pwn-editor">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C]" data-testid="pwn-title">Prior Written Notice</h2>
          <p className="text-sm text-[#595959] mt-1">{section?.description}</p>
          <div className="flex items-center gap-2 mt-2">
            <Badge className="bg-[#3B6A9B]/10 text-[#3B6A9B] text-xs">{filledCount}/{PWN_FIELDS.length} sections completed</Badge>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#3B6A9B] hover:bg-[#2D5480] text-white rounded-xl shrink-0" data-testid="pwn-save-top">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      {/* AI PWN Drafting Panel */}
      <div className="bg-gradient-to-r from-[#3B6A9B]/5 to-[#5A8A5A]/5 border border-[#3B6A9B]/20 rounded-2xl overflow-hidden" data-testid="ai-pwn-panel">
        <button className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-white/40 transition-colors" onClick={() => setAiOpen(!aiOpen)} data-testid="ai-pwn-toggle">
          <div className="flex items-center gap-2.5">
            <Sparkles className="h-4 w-4 text-[#3B6A9B]" />
            <span className="text-sm font-medium text-[#1C1C1C]">AI PWN Draft</span>
            <Badge className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200">Advisory Only</Badge>
            {fieldDrafts && <Badge className="text-[10px] bg-[#3B6A9B]/10 text-[#3B6A9B]">{fieldDrafts.length} field drafts</Badge>}
          </div>
          {aiOpen ? <ChevronUp className="h-4 w-4 text-[#595959]" /> : <ChevronDown className="h-4 w-4 text-[#595959]" />}
        </button>

        {aiOpen && (
          <div className="px-5 pb-5 border-t border-[#3B6A9B]/10">
            <div className="flex items-center justify-between mt-3 mb-4">
              <p className="text-xs text-[#595959] max-w-lg">
                AI drafts each PWN field using IEP decisions, present levels, goals, accommodations, services, and evaluation data. All drafts require team review and finalization.
              </p>
              <Button onClick={generateDrafts} disabled={generating}
                className="bg-[#3B6A9B] hover:bg-[#2D5480] text-white rounded-xl text-xs h-8 shrink-0" data-testid="generate-pwn-btn">
                {generating ? <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" />Drafting...</>
                  : <><Sparkles className="h-3 w-3 mr-1.5" />{fieldDrafts ? 'Regenerate' : 'Generate PWN Draft'}</>}
              </Button>
            </div>

            {aiMeta?.error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 flex items-start gap-2" data-testid="ai-pwn-error">
                <AlertTriangle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                <p className="text-xs text-red-700">{aiMeta.error}</p>
              </div>
            )}

            {fieldDrafts && fieldDrafts.length > 0 && (
              <>
                {aiMeta && !aiMeta.error && (
                  <div className="bg-white/60 border border-[#E5E5E5] rounded-xl p-3 mb-4" data-testid="ai-pwn-meta">
                    <div className="flex items-start gap-4 text-xs text-[#595959]">
                      <div className="flex-1">
                        {aiMeta.notes && <p className="mb-1"><strong>AI Notes:</strong> {aiMeta.notes}</p>}
                        {aiMeta.dataSources?.length > 0 && <p className="mb-1"><strong>Data used:</strong> {aiMeta.dataSources.join(', ')}</p>}
                        {aiMeta.gaps?.length > 0 && <p className="text-amber-600"><strong>Data gaps:</strong> {aiMeta.gaps.join('; ')}</p>}
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-[10px] text-[#595959]">{pendingCount} pending</span>
                        {pendingCount > 0 && (
                          <Button variant="outline" size="sm" className="rounded-lg text-xs h-7 border-green-300 text-green-700 hover:bg-green-50" onClick={acceptAllPending} data-testid="accept-all-pwn-btn">
                            <CheckCircle2 className="h-3 w-3 mr-1" />Accept All
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {aiMeta?.inputSnapshot && (
                  <div className="mb-3 text-[10px] text-[#595959]/60 flex items-center gap-2">
                    <Info className="h-3 w-3" />
                    <span>Context: {aiMeta.inputSnapshot.context_type} IEP | Sources: {aiMeta.inputSnapshot.data_sources?.join(', ') || 'none'} | Intensity: {aiMeta.inputSnapshot.ai_intensity}</span>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* PWN Structured Fields */}
      <div className="space-y-4">
        {PWN_FIELDS.map((field, idx) => {
          const draft = getDraftForField(field.key);
          const state = draftStates[field.key];
          const source = getFieldSource(field.key);
          const hasPendingDraft = draft && state === 'pending';

          return (
            <div key={field.key} className="bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden" data-testid={`pwn-field-${field.key}`}>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-bold text-[#3B6A9B] bg-[#3B6A9B]/10 rounded-full w-5 h-5 flex items-center justify-center shrink-0">{idx + 1}</span>
                  <label className="text-sm font-medium text-[#1C1C1C]">{field.label}</label>
                  {(content[field.key] || '').trim() && (
                    <span className="text-[10px] text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Completed</span>
                  )}
                  {source === 'ai_suggested' && (
                    <Badge className="text-[10px] bg-[#3B6A9B]/10 text-[#3B6A9B] border border-[#3B6A9B]/20">AI</Badge>
                  )}
                  {source === 'ai_edited' && (
                    <Badge className="text-[10px] bg-blue-50 text-blue-600 border border-blue-200">AI Edited</Badge>
                  )}
                </div>
                <p className="text-xs text-[#595959] mb-3 ml-7">{field.hint}</p>
                <Textarea
                  value={content[field.key] || ''}
                  onChange={e => updateField(field.key, e.target.value)}
                  placeholder={field.hint}
                  rows={3}
                  className="rounded-xl border-[#E5E5E5] text-sm"
                  data-testid={`pwn-input-${field.key}`}
                />
              </div>

              {/* AI Draft Suggestion for this field */}
              {hasPendingDraft && (
                <div className="border-t border-[#E5E5E5] bg-gradient-to-r from-[#3B6A9B]/[0.03] to-transparent px-5 py-4" data-testid={`pwn-draft-${field.key}`}>
                  <div className="flex items-start gap-3">
                    <Sparkles className="h-3.5 w-3.5 text-[#3B6A9B] mt-1 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] font-semibold text-[#3B6A9B] uppercase tracking-wider">AI Suggestion</span>
                        {draft.confidence && (
                          <Badge className={`text-[10px] border ${CONFIDENCE_STYLE[draft.confidence] || CONFIDENCE_STYLE.low}`}>
                            {draft.confidence}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-[#1C1C1C] mb-2 whitespace-pre-wrap leading-relaxed">{draft.draft_text}</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-[#595959] mb-3">
                        {draft.data_basis && <span><strong>Data:</strong> {draft.data_basis}</span>}
                        {draft.justification && <span><strong>Rationale:</strong> {draft.justification}</span>}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Button variant="outline" size="sm" onClick={() => acceptDraft(field.key)}
                          className="h-7 rounded-lg text-xs text-green-700 border-green-300 hover:bg-green-50" data-testid={`accept-pwn-${field.key}`}>
                          <CheckCircle2 className="h-3 w-3 mr-1" />Accept
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => editDraft(field.key)}
                          className="h-7 rounded-lg text-xs text-blue-600 border-blue-300 hover:bg-blue-50" data-testid={`edit-pwn-${field.key}`}>
                          <Edit3 className="h-3 w-3 mr-1" />Accept & Edit
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => rejectDraft(field.key)}
                          className="h-7 rounded-lg text-xs text-red-400 border-red-200 hover:bg-red-50" data-testid={`reject-pwn-${field.key}`}>
                          <XCircle className="h-3 w-3 mr-1" />Reject
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Resolved draft status */}
              {draft && state && state !== 'pending' && (
                <div className={`border-t px-5 py-2 flex items-center gap-2 text-[10px] ${
                  state === 'accepted' ? 'border-green-100 bg-green-50/30 text-green-600' :
                  state === 'edited' ? 'border-blue-100 bg-blue-50/30 text-blue-600' :
                  'border-gray-100 bg-gray-50/30 text-gray-400'
                }`} data-testid={`pwn-draft-status-${field.key}`}>
                  {state === 'accepted' && <><CheckCircle2 className="h-3 w-3" />Draft accepted</>}
                  {state === 'edited' && <><Edit3 className="h-3 w-3" />Draft accepted for editing</>}
                  {state === 'rejected' && <><XCircle className="h-3 w-3" />Draft rejected</>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex justify-end pt-4 border-t border-[#E5E5E5]">
        <Button onClick={handleSave} disabled={saving} className="bg-[#3B6A9B] hover:bg-[#2D5480] text-white rounded-xl" data-testid="pwn-save-bottom">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Prior Written Notice'}
        </Button>
      </div>
    </div>
  );
}
